import { memo } from "react";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../../locales/messages";
import { reviewTokens } from "../../theme";
import { LazyThumb } from "../LazyThumb";
import { ItemSummary } from "./useFlyerItemSummaries";

interface Props {
  summary: ItemSummary | "loading" | undefined;
  label?: string;
}

export const FlyerItemMiniPreview = memo(function FlyerItemMiniPreview({
  summary,
  label,
}: Props) {
  const { t } = useTranslation();

  if (summary === "loading" || summary === undefined) {
    return (
      <Stack spacing={0.75}>
        {label ? (
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
        ) : null}
        <Stack direction="row" spacing={1} alignItems="center">
          <Skeleton variant="rounded" width={48} height={48} />
          <Box flex={1}>
            <Skeleton width="70%" height={18} />
            <Skeleton width="45%" height={14} />
          </Box>
        </Stack>
      </Stack>
    );
  }

  if (summary.error) {
    return (
      <Typography variant="body2" color="text.secondary">
        {label ? `${label} ` : ""}
        {t(messages.Review_Manual_Cr_Swap_Item_Unavailable(), {
          id: summary.id.slice(0, 8) + "…",
        })}
      </Typography>
    );
  }

  const displayName = summary.name || t(messages.Unnamed_Item());
  const initialLetter = (displayName.trim()[0] || "?").toUpperCase();
  const priceParts: string[] = [];
  if (summary.salePrice != null) {
    priceParts.push(`${t(messages.Sale_Price())}: ${summary.salePrice}`);
  }
  if (summary.regularPrice != null) {
    priceParts.push(`${t(messages.Regular_Price())}: ${summary.regularPrice}`);
  }

  return (
    <Stack spacing={0.75}>
      {label ? (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
          {label}
        </Typography>
      ) : null}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          p: 0.75,
          borderRadius: `${reviewTokens.radiusSm}px`,
          border: `1px solid ${reviewTokens.border}`,
          bgcolor: "background.paper",
        }}
      >
        <LazyThumb
          src={summary.thumbnailUrl}
          size={48}
          fit="cover"
          radius={reviewTokens.radiusSm}
          fallback={
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: reviewTokens.slate500,
                fontWeight: 600,
                fontSize: 18,
              }}
            >
              {initialLetter}
            </Box>
          }
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, lineHeight: 1.35 }}
            noWrap
            title={displayName}
          >
            {displayName}
          </Typography>
          {summary.nameChinese ? (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              title={summary.nameChinese}
              display="block"
            >
              {summary.nameChinese}
            </Typography>
          ) : null}
          {summary.rank != null ? (
            <Typography variant="caption" color="text.secondary" display="block">
              {t(messages.Item_Rank(), { rank: summary.rank })}
            </Typography>
          ) : null}
          {priceParts.length > 0 ? (
            <Typography variant="caption" color="text.secondary" display="block">
              {priceParts.join(" · ")}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Stack>
  );
});
